import type { Nullable } from 'corgi-types';
import dayjs from 'dayjs';

import type { Translate } from '../../../providers/LocaleProvider';
import type { BirthDayFieldValue } from '../../Form/BirthdayField/types';
import { getValidDate } from '../../Form/BirthdayField/validations';
import type {
  FormFieldValidationRules,
  ValidateAgeOptions,
  ValidatePatternOptions,
  ValidateRulesOption,
  ValidationRuleAge,
  ValidationRuleGroup,
  ValidationRulePattern,
  ValidatorRule,
} from '../types';
import { ValidationRuleType } from '../types';
import { getPatternErrorMessage } from './get-error-message';
import { inputIsMatching } from './regex-helpers';

const validateNestedRules = ({ rules, inputValue, translate }: ValidateRulesOption) => {
  const nestedRules = rules?.filter(rule => 'rules' in rule);

  if (!!nestedRules?.length) {
    return nestedRules.map(rule => createValidator(rule, translate)(inputValue));
  }

  return null;
};

const findErrorMessage = (validatorNestedRules: Nullable<Array<string | boolean>>) =>
  validatorNestedRules?.find(validator => typeof validator === 'string');

const validatePattern = ({ rule, inputValue, translate }: ValidatePatternOptions) =>
  inputIsMatching(rule.value, inputValue) ||
  getPatternErrorMessage({ rule, inputValue, translate });

const validateNot = ({ rules, inputValue, translate }: ValidateRulesOption) => {
  const nestedValidationResults = validateNestedRules({
    rules,
    inputValue,
    translate,
  });
  const nestedRulesErrorMsg = findErrorMessage(nestedValidationResults);
  const failingPatternRule = rules?.find(
    rule => 'value' in rule && inputIsMatching(rule.value, inputValue)
  );

  if (!failingPatternRule && !nestedRulesErrorMsg) {
    return true;
  }

  return nestedRulesErrorMsg || (failingPatternRule as ValidationRulePattern)?.errorMessage;
};

const validateAllOf = ({ rules, inputValue, translate }: ValidateRulesOption) => {
  const nestedValidationResults = validateNestedRules({
    rules,
    inputValue,
    translate,
  });
  const nestedRulesErrorMsg = findErrorMessage(nestedValidationResults);
  const failingPatternRule = rules?.find(
    rule => 'value' in rule && !inputIsMatching(rule.value, inputValue)
  );

  if (!failingPatternRule && !nestedRulesErrorMsg) {
    return true;
  }

  return (
    nestedRulesErrorMsg ||
    getPatternErrorMessage({
      rule: failingPatternRule as ValidationRulePattern,
      inputValue,
      translate,
    })
  );
};

const validateAnyOf = ({ rules, inputValue, translate }: ValidateRulesOption) => {
  const nestedValidationResults = validateNestedRules({
    rules,
    inputValue,
    translate,
  });
  const isAnyOfNestedRulesValid = nestedValidationResults?.some(isValid => isValid === true);
  const isAnyOfValid = rules?.some(
    rule => 'value' in rule && inputIsMatching(rule.value, inputValue)
  );
  const nestedRulesErrorMsg = findErrorMessage(nestedValidationResults);

  if (isAnyOfValid || isAnyOfNestedRulesValid) {
    return true;
  }

  return (
    nestedRulesErrorMsg ||
    getPatternErrorMessage({
      rule: rules[0] as ValidationRulePattern,
      inputValue,
      translate,
    })
  );
};

const validateAge = ({ rules, inputValue, translate }: ValidateAgeOptions) => {
  const date = getValidDate(translate)(inputValue);

  if (date && rules.min && rules.max) {
    const isWithinRange =
      !(dayjs().diff(date, 'year') < rules.min) && !(dayjs().diff(date, 'year') > rules.max);

    return isWithinRange || rules.errorMessage;
  }

  return true;
};

const createValidator =
  (rule: ValidatorRule, translate: Translate) =>
  (inputValue: string | Record<string, string>): string | boolean => {
    switch (rule.ruleType) {
      case ValidationRuleType.Pattern:
        return validatePattern({
          rule: rule as ValidationRulePattern,
          inputValue: inputValue as string,
          translate,
        });
      case ValidationRuleType.Not:
        return validateNot({
          rules: (rule as ValidationRuleGroup).rules,
          inputValue: inputValue as string,
          translate,
        });
      case ValidationRuleType.AllOf:
        return validateAllOf({
          rules: (rule as ValidationRuleGroup).rules,
          inputValue: inputValue as string,
          translate,
        });
      case ValidationRuleType.AnyOf:
        return validateAnyOf({
          rules: (rule as ValidationRuleGroup).rules,
          inputValue: inputValue as string,
          translate,
        });
      case ValidationRuleType.Age:
        return validateAge({
          rules: rule as ValidationRuleAge,
          inputValue: inputValue as unknown as BirthDayFieldValue,
          translate,
        });
      default:
        return true;
    }
  };

export const getFormFieldValidators = (
  rules: FormFieldValidationRules['rules'],
  translate: Translate
) => {
  const validators: Record<
    string,
    (inputValue: string | Record<string, string>) => string | boolean
  > = {};

  rules?.forEach((rule, index) => {
    validators[`${rule.ruleType}${index}`] = createValidator(rule as ValidatorRule, translate);
  });

  return validators;
};
